import { format, formatDistanceToNow, isToday, isTomorrow, isYesterday, addDays, isBefore, isAfter, startOfDay } from 'date-fns';
import type { Timestamp } from 'firebase/firestore';

// Currency formatting
export function formatCurrency(amountInCents: number, currency: string = 'USD'): string {
  const amount = amountInCents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(amount);
}

export function parseCurrencyToCents(value: string | number): number {
  if (typeof value === 'number') {
    return Math.round(value * 100);
  }
  const cleaned = value.replace(/[^0-9.-]/g, '');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : Math.round(parsed * 100);
}

export function centsToDecimal(cents: number): number {
  return cents / 100;
}

// Date formatting
export function formatDate(date: Date | Timestamp, formatStr: string = 'MMM d, yyyy'): string {
  const d = date instanceof Date ? date : date.toDate();
  return format(d, formatStr);
}

export function formatRelativeDate(date: Date | Timestamp): string {
  const d = date instanceof Date ? date : date.toDate();

  if (isToday(d)) return 'Today';
  if (isTomorrow(d)) return 'Tomorrow';
  if (isYesterday(d)) return 'Yesterday';

  return formatDistanceToNow(d, { addSuffix: true });
}

export function formatDateTime(date: Date | Timestamp): string {
  const d = date instanceof Date ? date : date.toDate();
  return format(d, 'MMM d, yyyy h:mm a');
}

export function formatTime(date: Date | Timestamp): string {
  const d = date instanceof Date ? date : date.toDate();
  return format(d, 'h:mm a');
}

// Date utilities
export function isOverdue(date: Date | Timestamp): boolean {
  const d = date instanceof Date ? date : date.toDate();
  return isBefore(startOfDay(d), startOfDay(new Date()));
}

export function isDueWithin(date: Date | Timestamp, days: number): boolean {
  const d = date instanceof Date ? date : date.toDate();
  const now = new Date();
  const futureDate = addDays(now, days);
  return isAfter(d, now) && isBefore(d, futureDate);
}

export function getDaysUntil(date: Date | Timestamp): number {
  const d = date instanceof Date ? date : date.toDate();
  const now = new Date();
  const diffTime = d.getTime() - now.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

// String utilities
export function truncate(str: string, length: number): string {
  if (str.length <= length) return str;
  return str.slice(0, length) + '...';
}

export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function slugify(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Validation utilities
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function isValidPassword(password: string): { valid: boolean; message?: string } {
  if (password.length < 8) {
    return { valid: false, message: 'Password must be at least 8 characters' };
  }
  if (!/[A-Z]/.test(password)) {
    return { valid: false, message: 'Password must contain at least one uppercase letter' };
  }
  if (!/[a-z]/.test(password)) {
    return { valid: false, message: 'Password must contain at least one lowercase letter' };
  }
  if (!/[0-9]/.test(password)) {
    return { valid: false, message: 'Password must contain at least one number' };
  }
  return { valid: true };
}

// Color utilities - Tailwind -200 shades for consistency with category colors
export const NOTE_COLORS = {
  default: '#ffffff',
  gray: '#e5e7eb',
  red: '#fecaca',
  orange: '#fed7aa',
  amber: '#fde68a',
  yellow: '#fef08a',
  lime: '#d9f99d',
  green: '#bbf7d0',
  teal: '#99f6e4',
  cyan: '#a5f3fc',
  blue: '#bfdbfe',
  indigo: '#c7d2fe',
  purple: '#e9d5ff',
  pink: '#fbcfe8',
} as const;

export function getNoteColorValue(color: keyof typeof NOTE_COLORS): string {
  return NOTE_COLORS[color] || NOTE_COLORS.default;
}

// Category utilities
export const EXPENSE_CATEGORIES = {
  food: { label: 'Food & Dining', icon: 'utensils' },
  transport: { label: 'Transportation', icon: 'car' },
  shopping: { label: 'Shopping', icon: 'shopping-bag' },
  entertainment: { label: 'Entertainment', icon: 'film' },
  utilities: { label: 'Utilities', icon: 'home' },
  healthcare: { label: 'Healthcare', icon: 'heart' },
  education: { label: 'Education', icon: 'book' },
  travel: { label: 'Travel', icon: 'plane' },
  other: { label: 'Other', icon: 'more-horizontal' },
} as const;

export const DATE_TYPES = {
  birthday: { label: 'Birthday', icon: 'cake' },
  anniversary: { label: 'Anniversary', icon: 'heart' },
  holiday: { label: 'Holiday', icon: 'star' },
  meeting: { label: 'Meeting', icon: 'users' },
  deadline: { label: 'Deadline', icon: 'alert-circle' },
  reminder: { label: 'Reminder', icon: 'bell' },
  other: { label: 'Other', icon: 'calendar' },
} as const;

export const PRIORITY_COLORS = {
  high: 'text-red-600 bg-red-100',
  medium: 'text-yellow-600 bg-yellow-100',
  low: 'text-green-600 bg-green-100',
} as const;

// File utilities
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export function getFileExtension(filename: string): string {
  return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2);
}

export function isImageFile(mimeType: string): boolean {
  return mimeType.startsWith('image/');
}

export function isPdfFile(mimeType: string): boolean {
  return mimeType === 'application/pdf';
}

// ID generation
export function generateId(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

// Effort Time utilities
export * from './effortTime';

// Recurrence utilities
export * from './recurrenceUtils';

// Sub-Item defaults
export * from './subItemDefaults';

// Item Group utilities
export * from './itemGroupUtils';
